// src/components/UploadModal.jsx
//
// Wave 2.5 (22 Apr 2026): simplified upload modal for matter briefs.
// Opened from inside MatterView (AwaitingPanel first-brief upload, or DocsTab
// upload button). Matter already exists, so matter_id, matter_name, and
// perspective are not captured here.
//
// ATTEST-1 (12 Jun 2026): upload tick RETIRED — the single run attestation now
// lives on RunConfirmModal (authority + suppression/Harman, bound to the
// crystallised document manifest at run confirmation). T&Cs + AUP + Clause B
// acceptance lives at signup. matter_documents v3 acknowledgement columns
// remain in schema, no longer populated from here.
//
// B-1 (15 Jun 2026): MULTI-FILE upload. Previously the drop/picker handlers
// read only files[0], so selecting N files silently kept 1 — a silent
// data-loss bug (lawyer pays + runs on a partial set believing it complete).
// Now: accept all selected/dropped files, upload each sequentially through the
// existing generateUploadUrl -> uploadToS3 -> confirmUpload sequence, and show
// per-file status so the user sees exactly what landed before closing.
// "Fail loud, or succeed completely" — no silent discard.
//
// Props:
//   matter      (required) — { matter_id, matter_name, ... } from parent
//   token       (required) — Cognito id token for API auth
//   onClose     (required) — () => void
//   onUploaded  (required) — () => void; parent refreshes matter state (called
//                            once after the batch completes)

import React, { useState, useRef, useCallback } from 'react';
import { generateUploadUrl, uploadToS3, confirmUpload } from '../api';

const ACCEPTED = '.pdf,.docx';
const MAX_FILES = 20; // sane per-batch cap; prevents a folder-drop locking the modal for minutes

// Per-file row status: queued | uploading | done | error
function newItem(file) {
  return { file, name: file.name, size: file.size, status: 'queued', error: '' };
}

export default function UploadModal({ matter, token, onClose, onUploaded }) {
  // items: array of { file, name, size, status, error }
  const [items,    setItems]    = useState([]);
  const [dragging, setDragging] = useState(false);
  const [busy,     setBusy]     = useState(false);
  const [notice,   setNotice]   = useState(''); // loud, user-facing rejections (type / count)
  const inputRef = useRef();

  const anyUploaded = items.some(i => i.status === 'done');
  const allSettled  = items.length > 0 && items.every(i => i.status === 'done' || i.status === 'error');
  const canSubmit   = items.length > 0 && !busy && !allSettled;

  // Add files (from picker or drop). Fails LOUD, never silent:
  //  - wrong file types are reported, not dropped without a word
  //  - a count cap prevents a folder-drop locking the modal for minutes
  //  - de-dupes by name+size so a double-add doesn't create duplicate rows
  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setNotice('');

    const rejected = [];
    setItems(prev => {
      const seen = new Set(prev.map(i => `${i.name}:${i.size}`));
      const accepted = [];
      for (const f of incoming) {
        const isDoc = /\.(pdf|docx)$/i.test(f.name);
        if (!isDoc) { rejected.push(f.name); continue; }          // wrong type — report, don't silently keep
        const key = `${f.name}:${f.size}`;
        if (seen.has(key)) continue;                               // duplicate — skip quietly (same file)
        seen.add(key);
        accepted.push(f);
      }

      let next = [...prev, ...accepted.map(newItem)];
      let capMsg = '';
      if (next.length > MAX_FILES) {
        capMsg = `Up to ${MAX_FILES} files at a time. Extra files were not added — upload the rest in a second batch.`;
        next = next.slice(0, MAX_FILES);
      }

      // Build the loud notice AFTER we know what was rejected/capped.
      const msgs = [];
      if (rejected.length) msgs.push(`Skipped (only PDF or DOCX allowed): ${rejected.join(', ')}`);
      if (capMsg) msgs.push(capMsg);
      if (msgs.length) setNotice(msgs.join(' '));

      return next;
    });
  }, []);

  const onDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  }, [addFiles]);

  const onDragOver  = e => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const removeItem = (idx) => {
    if (busy) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  function setItemStatus(idx, status, error = '') {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, status, error } : it));
  }

  async function handleUpload() {
    if (!canSubmit) return;
    setBusy(true);
    let uploadedCount = 0;

    // Sequential upload — each file through the existing 3-call sequence.
    for (let i = 0; i < items.length; i++) {
      // skip ones already done (allows re-submit after a partial failure)
      if (items[i].status === 'done') continue;
      try {
        setItemStatus(i, 'uploading');
        const { upload_url, s3_key } = await generateUploadUrl(token, matter.matter_id);
        await uploadToS3(upload_url, items[i].file);
        await confirmUpload(token, matter.matter_id, s3_key, items[i].file.name);
        setItemStatus(i, 'done');
        uploadedCount += 1;
      } catch (e) {
        console.error('Upload failed for', items[i].name, e);
        // Friendly message for an expired Cognito session (until the global
        // api.js 401 interceptor exists). Otherwise surface the raw message.
        const is401 = e?.status === 401 || e?.response?.status === 401 || /401/.test(e?.message || '');
        const msg = is401
          ? 'Your session has expired — please refresh the page and sign in again.'
          : (e.message || 'Upload failed');
        setItemStatus(i, 'error', msg);
      }
    }

    setBusy(false);
    if (uploadedCount > 0 && onUploaded) onUploaded();
    // Do NOT auto-close: let the user SEE the per-file result (esp. any errors)
    // before closing. They close via the footer button.
  }

  const statusBadge = (it) => {
    switch (it.status) {
      case 'done':      return <span style={{ color: '#2D8A4E', fontSize: 16 }}>✓</span>;
      case 'uploading': return <span style={{ color: '#8A93A1', fontSize: 12 }}>…</span>;
      case 'error':     return <span style={{ color: '#B03030', fontSize: 14 }}>✕</span>;
      default:          return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span>Upload documents</span>
          <button className="modal-close" onClick={onClose} disabled={busy}>×</button>
        </div>

        <div className="modal-body">
          {/* Matter context (read-only display) */}
          <div style={{
            fontSize: 12.5, color: '#B0B8C4', marginBottom: 14,
            padding: '8px 12px', background: 'var(--navy-soft)', border: '1px solid var(--border)', borderRadius: 6,
          }}>
            Uploading to: <strong style={{ color: 'var(--text)', fontWeight: 600 }}>{matter.matter_name}</strong>
          </div>

          {/* Drop zone */}
          <div
            className={`dropzone ${dragging ? 'dropzone-active' : ''} ${items.length ? 'dropzone-filled' : ''}`}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => !busy && inputRef.current?.click()}
            style={{ cursor: busy ? 'default' : 'pointer' }}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              multiple
              style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              disabled={busy}
            />
            <div className="dz-icon">⬆</div>
            <div className="dz-hint">Drag files here or click to browse</div>
            <div className="dz-hint2">PDF or DOCX — raw documents, not summaries.</div>
            <div className="dz-hint2" style={{ marginTop: 4, opacity: 0.8 }}>To add several at once, hold Ctrl (or Cmd) and click each file, or drag a multiple selection.</div>
          </div>

          {/* Loud notice: wrong types skipped, or count cap hit */}
          {notice && (
            <div className="status-err" style={{ marginTop: 12 }}>{notice}</div>
          )}

          {/* Selected / uploaded file list */}
          {items.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: '#8A93A1', marginBottom: 8 }}>
                {items.length} file{items.length === 1 ? '' : 's'} selected
                {anyUploaded ? ` · ${items.filter(i => i.status === 'done').length} uploaded` : ''}
              </div>
              {items.map((it, idx) => (
                <div key={`${it.name}-${idx}`} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', marginBottom: 6,
                  background: 'var(--navy-soft)', border: '1px solid var(--border)', borderRadius: 6,
                }}>
                  <div style={{ fontSize: 14 }}>📄</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#8A93A1' }}>
                      {(it.size / 1024).toFixed(0)} KB
                      {it.status === 'error' ? ` — ${it.error}` : ''}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, minWidth: 20, textAlign: 'center' }}>
                    {statusBadge(it)}
                  </div>
                  {/* Remove only before the batch runs (idle queued rows) */}
                  {!busy && it.status !== 'done' && (
                    <button
                      onClick={() => removeItem(idx)}
                      style={{ background: 'none', border: 'none', color: '#8A93A1', cursor: 'pointer', fontSize: 13, flexShrink: 0 }}
                      title="Remove"
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ATTEST-1 — attestation now lives at run confirmation */}
          <div style={{
            fontSize: 12, color: '#8A93A1', fontWeight: 300, lineHeight: 1.5,
            marginTop: 14, padding: '0 2px',
          }}>
            After uploading, check the Documents tab and confirm every document you
            intend to rely on is listed before you run the matter. You'll confirm
            your authorisation at the run confirmation step.
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>
            {allSettled ? 'Close' : 'Cancel'}
          </button>
          {!allSettled && (
            <button
              className="btn-primary"
              onClick={handleUpload}
              disabled={!canSubmit}
              title={!canSubmit ? 'Add at least one file to continue' : ''}
            >
              {busy ? 'Uploading…' : `Upload ${items.length || ''}`.trim()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
